import { Button, Col, Flex, Form, Input, Modal, Row, Space } from "antd";
import { useForm } from "antd/es/form/Form";
import FormItem from "antd/es/form/FormItem";
import FormAntd from "components/form/FormProvide/FormAntd";
import { useTranslation } from "react-i18next";
import { isPassword } from "utils/validators";

const ModalChangePassword = ({
    isModalOpen = false,
    setIsModalOpen = () => { },
    onSubmitChangeModal = () => { }
}) => {
    const { t } = useTranslation();
    const [form] = useForm();

    function matchColorText(bool) {
        return !bool ? 'grey' : 'black'
    }

    function matchColorDot(bool) {
        return !bool ? 'grey' : '#2CADE7'
    }

    const isPasswordValid = (password) => {
        if (!password || password.length < 8) return false;

        const hasUpperCase = /(?=.*[A-Z])/.test(password);
        const hasLowerCase = /(?=.*[a-z])/.test(password);
        const hasNumber = /(?=.*[0-9])/.test(password);
        const hasSpecialChar = /(?=.*[^a-zA-Z0-9])/.test(password);

        return hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
    };

    const validatePassword = (_, value) => {
        if (!value) {
            return Promise.reject(new Error(t("change_password.new_password_is_at")));
        }
        if (value.length < 8) {
            return Promise.reject(new Error(t("change_password.at_leat_8")));
        }
        if (!isPassword(value)) {
            return Promise.reject(new Error(t("change_password.password_invalid")));
        }
        return Promise.resolve();
    };

    const validateConfirmPassword = (_, value) => {
        if (!value) {
            return Promise.reject(new Error(t("change_password.new_password_confirm")));
        }
        const newPassword = form.getFieldValue("newPassword");
        if (value !== newPassword) {
            return Promise.reject(new Error(t("change_password.must_match")));
        }
        return Promise.resolve();
    };

    const handleSubmit = (values) => {
        onSubmitChangeModal(values);
        form.resetFields();
        setIsModalOpen(false);
    };

    return (
        <Modal
            title={<span style={{ color: "#42526D" }}>{t("action.change_password")}</span>}
            open={isModalOpen}
            closable={false}
            maskClosable={false}
            footer={null}
            destroyOnClose
        >
            <FormAntd
                form={form}
                onFinish={handleSubmit}
            >
                <FormItem shouldUpdate={(prev, cur) =>
                    prev.newPassword !== cur.newPassword || prev.newPasswordConfirm !== cur.newPasswordConfirm
                }>
                    {({ getFieldValue, setFieldValue, resetForm }) => {
                        const newPassword = getFieldValue("newPassword") || "";
                        const newPasswordConfirm = getFieldValue("newPasswordConfirm") || "";

                        const isNewPasswordValid = isPasswordValid(newPassword);
                        const isConfirmMatched = newPasswordConfirm && newPassword === newPasswordConfirm;
                        const canSubmit = isNewPasswordValid && isConfirmMatched;


                        return (
                            <Space direction="vertical" size={24} style={{ width: "100%" }}>
                                <Flex vertical gap={16}>
                                    <Form.Item
                                        label={t("change_password.newPassword")}
                                        name="newPassword"
                                        rules={[{ validator: validatePassword }]}
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Input.Password
                                            size="large"
                                            placeholder={t("common.please_fill")}
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label={t("change_password.newPasswordConfirm")}
                                        name="newPasswordConfirm"
                                        rules={[{ validator: validateConfirmPassword }]}
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                    >
                                        <Input.Password
                                            size="large"
                                            placeholder={t("common.please_fill")}
                                        />
                                    </Form.Item>
                                </Flex>

                                <Flex>
                                    <Row gutter={10} justify={"space-between"}>
                                        <Col span={12}>
                                            <Space styles={{ item: { fontSize: '10px', color: matchColorText(newPassword?.match("(?=.*[A-Z])")) } }}>
                                                <div style={{ borderRadius: "50%", width: "10px", height: "10px", backgroundColor: matchColorDot(newPassword?.match("(?=.*[A-Z])")) }}></div>
                                                {t("common.capital_pass")}
                                            </Space>
                                        </Col>
                                        <Col span={12}>
                                            <Space styles={{ item: { fontSize: '10px', color: matchColorText(newPassword?.match("(?=.*[a-z])")) } }}>
                                                <div style={{ borderRadius: "50%", width: "10px", height: "10px", backgroundColor: matchColorDot(newPassword?.match("(?=.*[a-z])")) }}></div>
                                                {t("common.lower_pass")}
                                            </Space>
                                        </Col>
                                        <Col span={12}>
                                            <Space styles={{ item: { fontSize: '10px', color: matchColorText(newPassword?.match("(?=.*[0-9])")) } }}>
                                                <div style={{ borderRadius: "50%", width: "10px", height: "10px", backgroundColor: matchColorDot(newPassword?.match("(?=.*[0-9])")) }}></div>
                                                {t("common.number_pass")}
                                            </Space>
                                        </Col>
                                        <Col span={12}>
                                            <Space styles={{ item: { fontSize: '10px', color: matchColorText(newPassword?.length >= 8) } }}>
                                                <div style={{ borderRadius: "50%", width: "10px", height: "10px", backgroundColor: matchColorDot(newPassword?.length >= 8) }}></div>
                                                {t("common.at least_8")}
                                            </Space>
                                        </Col>
                                        <Col span={12}>
                                            <Space styles={{ item: { fontSize: '10px', color: matchColorText(newPassword?.match("(?=.*[^a-zA-Z0-9])")) } }}>
                                                <div style={{ borderRadius: "50%", width: "10px", height: "10px", backgroundColor: matchColorDot(newPassword?.match("(?=.*[^a-zA-Z0-9])")) }}></div>
                                                {t("common.special_char")}
                                            </Space>
                                        </Col>
                                    </Row>
                                </Flex>

                                <Row justify={"end"} style={{ gap: "12px" }}>
                                    <Button
                                        size="large"
                                        type="default"
                                        onClick={() => {
                                            form.resetFields();
                                            setIsModalOpen(false);
                                        }}
                                        style={{ color: "#344054", fontWeight: "bold" }}
                                    >
                                        {t("common.cancel")}
                                    </Button>
                                    <Button
                                        htmlType="submit"
                                        type="primary"
                                        style={{ fontWeight: "bold", backgroundColor: !canSubmit ? null : "var(--button-color)" }}
                                        size="large"
                                        disabled={!canSubmit}
                                    >
                                        {t("common.submit")}
                                    </Button>
                                </Row>
                            </Space>
                        )
                    }}
                </FormItem>
            </FormAntd>
        </Modal >
    )

}

export default ModalChangePassword;